/**
 * **全局通告钩子表**（2026-10-24 通告事件批，茄子令「所有剧本都会触发的事件」）
 *
 * 它不是另一套结构，是 `PlotEventDef` 的**第五类触发口径**：通告生命周期信号
 * （`core/EventBus.emitNoticeHook`，type×when×refId 一份 payload）→ 查本表 → 掷 `chance` →
 * `PlotSystem.fire` 入队（带剧本上下文，插值 `{script}`/`{role}` 的来源）→ 宿主演出。
 * 与特殊剧本 `plotHooks` 的分工（⛔ 别混用）：那边＝**剧本专属叙事**（登记在剧本数据上、
 * 文案写死剧名角色名）；这边＝**通告通用事件**（登记在本表、文案模板＋插值，所有同类通告共享）。
 *
 * **份额闸**（2026-11-03 茄子令「同类型事件以月为单位的每月只触发一个，以周为单位的每周只触发一个，
 * 有满足条件固定触发的事件就不用再掷骰随机事件」）：
 *  - **一周一条不用新建状态**：一周只有一个 work 槽（`player.schedule[weekIdx]`），拍摄周与杀青天然错开
 *    （杀青周只发 `done` 不发 `shootWeek`），所以一次信号＝一周的份额，禁止再加计数。
 *  - **月一条**＝`per: 'month'`：本月已派发过任意月粒度条目就整月让位，判据读 `PlotMessage.pushedAbsMonth`
 *    （现成字段，禁止新增存档字段、禁止升 SAVE_VERSION）。
 *  - 同一次信号多条命中时，`PlotSystem.onNoticeHook` 把**必发的排在前**，它占了位后面的概率条连骰都不掷。
 *
 * **怎么加一条**（往数组里加，别的什么都不用改）：
 * ```ts
 * {
 *     type: 'drama',          // 通告类型（广告/杂志上线时扩 'NoticeHookPayload.type' 字面量）
 *     when: 'done',           // 时点：done=杀青 / shootWeek=拍摄周行程执行后（非杀青周）/ premiere=开播当月的月初
 *     event: 'NOTICE_XXX',    // FixedEvents 表里的事件 id（trigger 留空 + repeatable: true）
 *     chance: 0.6,            // 概率，缺省 1=必发（挂表不挂事件：概率属于这次触发，不属于那条文案）
 *     per: 'month',           // 周期粒度，缺省 'week'（见上面的份额闸）
 *     cond: { rank: 5, billingMax: 3, genres: ['tv', 'web'] },   // 咖位下限／番位上限／剧本类型（各读一个数据源）
 * }
 * ```
 * **事件本体的三条纪律**：
 *  - `repeatable: true` 必写——通用事件一个 id 服务所有通告，一次性灯（flags[event.id]）会把
 *    第二部剧的微博吞掉；限频靠「一部剧只杀青一次」这类自然事实＋fire 的在队查重（同剧在队不重复）。
 *  - 文案用插值不写死：`{script}` 剧名／`{role}` 角色名（缺本名回退角色定位）／`{player}` 昵称／`%玩家名%` 本名。
 *  - `playOn` 缺省＝执行档（杀青/拍摄周信号发生在 runWeek 内，月度执行页当周就能弹）；
 *    月初信号（premiere）配 `playOn: 'decision'`＝点确认行程之后、行程执行之前的决策页档。
 */
import type { NoticePlotHook } from '../types';

export const NOTICE_PLOT_HOOKS: NoticePlotHook[] = [
    // ===== 后援会·杀青应援微博（2026-10-24 首条；同日茄子令两改：必弹 + 门槛）=====
    // 我的戏份拍完那一周，后援会官宣杀青＋期待播出。**必发**（不写 chance＝1，概率版本曾 0.6 已撤）。
    // 门槛（2026-10-24 茄子令「角色是 3 番及 3 番以内且玩家咖位达到 LV5」）：
    // rank 5＝咖位 Lv.5 起（小透明时期后援会还发不了官杀青；前期咖位涨得快，Lv.5≈二线）；billingMax 3＝一番到三番
    // （四五番/群演杀青不够后援会专门发博的分量）。杀青周只发 done 信号不发 shootWeek（见 shootOneWeek），
    // 所以不会同周双弹两条通告微博。
    {
        type: 'drama',
        when: 'done',
        event: 'NOTICE_DRAMA_WRAP',
        cond: { rank: 5, billingMax: 3 },
    },

    // ===== 后援会·站子拍摄周返图（2026-11-03，文案源 docs/特殊剧情/通告通用事件.md 事件 2）=====
    // 拍摄周行程执行完那一周掷一次（杀青周只发 done，天然不与上面那条抢同一周）。
    // chance 0.25 的频次账：可触发周＝角色 shootWeeks − 1，所以 6 周戏≈1.3 次／部、4 周≈0.8 次／部、
    // 1 周的角色戏永不触发。rank 4＝Lv.4 起才有人给你建站子；billingMax 3＝一～三番（四番以下不拍下班图）。
    {
        type: 'drama',
        when: 'shootWeek',
        event: 'NOTICE_DRAMA_FANSITE',
        chance: 0.25,
        cond: { rank: 4, billingMax: 3 },
    },

    // ===== 后援会·播出期轮博护航（2026-11-03，文案源 同上 事件 3）=====
    // 开播当月的月初发（`ScriptSystem.notifyPremiereMonth`）：点确认行程之后、行程执行之前的决策页演。
    // 不写 chance＝必发；per 'month'＝本月已有月粒度条目就整月让位（同月两部一番剧开播只播一条）。
    // billingMax 1＝只有一番（轮博排班是一番番的体量）；genres 电视剧/网剧＝电影与综艺、短剧不走这条。
    {
        type: 'drama',
        when: 'premiere',
        event: 'NOTICE_DRAMA_PATROL',
        per: 'month',
        cond: { rank: 5, billingMax: 1, genres: ['tv', 'web'] },
    },
];
